import { apiClient } from '@lib/api/client';
import type { LoginRequest, LoginResponse, User } from '../types/auth.types';

/**
 * Authentication Service
 * Communicates with the BFF (Backend For Frontend) middleware
 * The BFF handles communication with Aludra API and filters sensitive data
 */
export const authService = {
  /**
   * Login user through BFF middleware
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>('/auth/login', credentials);
    return response.data;
  },

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    await apiClient.post('/auth/logout');
  },

  /**
   * Get current user information
   *
   * El BFF manda `isSuperAdmin` aparte de `user` (mismo shape que
   * `LoginResponse`) — hay que mezclarlo acá igual que hace el login
   * (ver `useAuth.ts`), si no, cada rehidratación por F5 (que pasa por
   * este método, no por login) pierde el `isSuperAdmin` real del usuario.
   */
  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get<{ user: User; isSuperAdmin?: boolean }>('/auth/me');
    return { ...response.data.user, isSuperAdmin: response.data.isSuperAdmin ?? false };
  },

  /**
   * Refresh access token
   * Refresh token is automatically sent via HTTP-only cookie
   */
  async refreshToken(): Promise<{ accessToken: string }> {
    const response = await apiClient.post<{ accessToken: string }>('/auth/refresh');
    return response.data;
  },

  /**
   * Validate current token
   */
  async validateToken(): Promise<{ valid: boolean }> {
    const response = await apiClient.post<{ valid: boolean }>('/auth/validate');
    return response.data;
  },

  /**
   * Forgot password
   * Send password reset email to user
   */
  async forgotPassword(
    email: string,
    tenant: string,
    tenantUrl: string,
    titleButton?: string
  ): Promise<{ message: string }> {
    // Ya no devuelve el userId: el backend lo guarda de su lado ligado al
    // correo y lo entrega solo dentro del ticket de verificación. Este
    // endpoint es público, así que devolverlo aquí era regalarle a cualquier
    // anónimo la mitad del material para resetear una cuenta ajena.
    const response = await apiClient.post<{ message: string }>('/auth/forgot-password', {
      email,
      tenant,
      tenantUrl,
      titleButton,
    });
    return response.data;
  },

  /**
   * Get user information by email
   * Used during password reset flow to get userId
   */
  async getUserByEmail(email: string, tenant?: string): Promise<any> {
    const response = await apiClient.post('/auth/get-user-by-email', {
      email,
      tenant,
    });
    return response.data;
  },

  /**
   * Verify the 4-digit reset-password code BEFORE resetting the password.
   * Paso que faltaba respecto al portal legado — sin esto mdl17 nunca
   * invalida el codigo y reenvia siempre el mismo en solicitudes repetidas.
   */
  async verifyResetCode(
    code: string,
    email: string
  ): Promise<{ valid: boolean; resetTicket: string }> {
    // Devuelve además el `resetTicket`: la prueba, emitida por el servidor, de
    // que este código se verificó. `resetPassword` no funciona sin él.
    const response = await apiClient.post<{ valid: boolean; resetTicket: string }>(
      '/auth/verify-reset-code',
      { code, email }
    );
    return response.data;
  },

  /**
   * Reset password con el ticket que devuelve `verifyResetCode`.
   *
   * El tenant, el userId y el código ya no se mandan: el backend los saca del
   * ticket e ignora lo que venga en el body. El ticket es de un solo uso.
   */
  async resetPassword(
    resetTicket: string,
    password: string,
    confirmPassword: string
  ): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>('/auth/reset-password', {
      resetTicket,
      password,
      confirmPassword,
    });
    return response.data;
  },

  /**
   * Confirm a new/invited user's account and set their initial password.
   * Usado desde el link de invitación (UserId/TenantId/Token en la URL,
   * ver SetNewPasswordPage.tsx) — distinto del self-service resetPassword.
   */
  async confirmEmail(
    tenant: string,
    userId: string,
    password: string,
    confirmPassword: string,
    code: string
  ): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>('/auth/confirm-email', {
      tenant,
      userId,
      password,
      confirmPassword,
      code,
    });
    return response.data;
  },
};
